import { Component, inject, signal, effect, untracked } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NExpeStore } from './nexpe.store';
import { NexpeSearchBarComponent } from './components/nexpe-search-bar/nexpe-search-bar.component';
import { NexpeTableComponent } from './components/nexpe-table/nexpe-table.component';
import { NexpeDetailModalComponent } from './components/nexpe-detail-modal/nexpe-detail-modal.component';
import { DeleteConfirmationModalComponent } from '../../shared/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { NExpeConfig } from './nexpe.model';

@Component({
  selector: 'app-nexpe',
  imports: [
    TranslateModule,
    NexpeSearchBarComponent,
    NexpeTableComponent,
    NexpeDetailModalComponent,
    DeleteConfirmationModalComponent,
  ],
  providers: [NExpeStore],
  templateUrl: './nexpe.component.html',
  styleUrl: './nexpe.component.scss',
})
export class NexpeComponent {
  private readonly store = inject(NExpeStore);

  // Store computed signals
  readonly isLoading = this.store.isLoading;
  readonly error = this.store.error;
  readonly filteredNexpeData = this.store.filteredNexpeData;
  readonly currentNExpe = this.store.currentNExpe;
  readonly modalOpen = this.store.modalOpen;

  // Component state
  readonly isEditMode = signal(false);
  readonly showDeleteModal = signal(false);
  readonly nexpeToDelete = signal<NExpeConfig | null>(null);

  private readonly shouldScrollAfterAdd = signal(false);

  constructor() {
    this.setupAutoScrollOnAdd();
  }

  // Search handling
  onSearchChange(query: string): void {
    this.store.setSearchQuery(query);
  }

  // Row selection
  onRowClick(nexpeId: string): void {
    this.isEditMode.set(true);
    this.store.openNExpeDetail(nexpeId);
  }

  // Add new NExpe
  onAddNExpe(): void {
    this.isEditMode.set(false);
    this.store.openNewNExpeModal();
  }

  // Modal handling
  onModalClose(): void {
    this.store.closeModal();
  }

  onModalSave(): void {
    const isNewNexpe = !this.isEditMode();

    if (isNewNexpe) {
      this.shouldScrollAfterAdd.set(true);
    }

    this.store.saveNExpeConfig();
  }

  onConfigChange(config: NExpeConfig): void {
    this.store.updateNExpeConfig(config);
  }

  // Delete handling
  onDeleteNExpe(nexpe: string): void {
    const nexpeConfig = this.currentNExpe();
    if (nexpeConfig) {
      this.nexpeToDelete.set(nexpeConfig);
      this.store.closeModal();
      this.showDeleteModal.set(true);
    }
  }

  onConfirmDelete(): void {
    const nexpe = this.nexpeToDelete();
    if (nexpe) {
      this.store.deleteNExpe(nexpe.nexpe);
      this.closeDeleteModal();
    }
  }

  onCancelDelete(): void {
    const nexpe = this.nexpeToDelete();
    if (nexpe) {
      this.store.openNExpeDetail(nexpe.nexpe);
    }
    this.closeDeleteModal();
  }

  private closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.nexpeToDelete.set(null);
  }

  //Auto scroll after validation and closed modal
  private setupAutoScrollOnAdd(): void {
    effect(() => {
      if (!this.shouldScrollAfterAdd()) return;

      if (!this.modalOpen() && !this.isLoading()) {
        untracked(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
              this.shouldScrollAfterAdd.set(false);
            });
          });
        });
      }
    });
  }
}
